import { useContext, useMemo } from 'react';
import DiscountContext from '~/context/vdp/DiscountContext';
import UserVehicleContext, {
  useVehiclePaymentTerms
} from '~/context/vdp/UserVehicleContext';
import {
  useGetVehicleDetails,
  useIsNewVehicle
} from '~/context/vdp/VehicleContext';
import useShowTradeIncentivesCopy from '~/hooks/common/selectors/useShowTradeIncentivesCopy';
import { usePriceDrop } from '~/hooks/vdp/index';
import useIsHighNetTrade from '~/hooks/vdp/useIsHighNetTrade';

type VehiclePrice = {
  finalPrice: number;
  originalPrice: number;
};

/**
 * Returns the price to display as strikethrough/comparison.
 * - New vehicles: MSRP
 * - Price drop: previous price
 * - Default: current price (no strikethrough shown)
 */
export function useOriginalPrice() {
  const { msrp = 0, previousPrice = 0, price = 0 } = useGetVehicleDetails();
  const { showPriceDrop } = usePriceDrop();
  const isNewVehicle = useIsNewVehicle();

  if (isNewVehicle) return msrp;
  if (showPriceDrop) return previousPrice;

  return price;
}

/**
 * Returns the offered price before customer-specific discounts are applied.
 * For new vehicles: includes manufacturer incentives (incentivizedPrice from pricing API)
 * For used vehicles: the listed price from GraphQL
 */
export function useOfferPrice() {
  const { vehiclePricingInfo } = useContext(UserVehicleContext);
  const { price = 0 } = useGetVehicleDetails();
  const { incentivizedPrice } = useVehiclePaymentTerms();
  const isNewVehicle = useIsNewVehicle();

  return useMemo(
    () =>
      isNewVehicle
        ? incentivizedPrice ?? (vehiclePricingInfo.isLoaded ? price : 0)
        : price,
    [incentivizedPrice, isNewVehicle, price, vehiclePricingInfo.isLoaded]
  );
}

/**
 * Hook to get the vehicle price to show in the VehicleInfoCard and sticky banner
 * @returns VehiclePrice object with finalPrice and originalPrice
 */
export const useVehiclePrice = (): VehiclePrice => {
  const { totalDiscount = 0 } = useContext(DiscountContext);
  const isHighNetTrade = useIsHighNetTrade();
  const { tradeIncentive = 0 } = useVehiclePaymentTerms();
  const { showTradeIncentive } = useShowTradeIncentivesCopy({ tradeIncentive });

  const shouldShowIncentive = showTradeIncentive && !isHighNetTrade;
  const offerPrice = useOfferPrice();
  const originalPrice = useOriginalPrice();

  return useMemo(() => {
    const allDiscounts = shouldShowIncentive
      ? totalDiscount + tradeIncentive
      : totalDiscount;

    return {
      finalPrice: offerPrice - allDiscounts,
      originalPrice
    };
  }, [
    offerPrice,
    originalPrice,
    shouldShowIncentive,
    totalDiscount,
    tradeIncentive
  ]);
};
